import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, BookOpen, Megaphone, Printer, Globe2, Headphones, Sparkles, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import MobileTopBar from "../components/MobileTopBar";
import ConsultModal, { hasSubmittedConsult } from "../components/ConsultModal";
import StudioFooter from "../components/StudioFooter";
import { QUICK_ACTIONS, SERVICES, totalSubServices } from "../lib/services";
import { useStudio } from "../context/StudioContext";
import { usePlanDrawer } from "../context/PlanDrawerContext";
import { readAbandonedCheckout, clearAbandonedCheckout } from "../lib/abandonedCheckout";
import { formatINR } from "../lib/pricing";
import { useOverrides, applySubOverride } from "../context/OverridesContext";

const ICONS = { BookOpen, Megaphone, Printer, Globe2, Headphones, Sparkles };

export default function Dashboard() {
  const navigate = useNavigate();
  const { email, plan } = useStudio();
  const { openDrawer } = usePlanDrawer();
  const [showConsult, setShowConsult] = useState(false);
  const [abandoned, setAbandoned] = useState(null);

  useEffect(() => {
    toast.success("Access granted. Welcome to the Studio.");
    let t;
    if (!hasSubmittedConsult()) {
      t = setTimeout(() => setShowConsult(true), 900);
    }
    setAbandoned(readAbandonedCheckout());
    return () => { if (t) clearTimeout(t); };
  }, []);

  return (
    <div className="flex min-h-screen bg-brand-ink">
      <Sidebar />

      <main className="flex-1 overflow-x-hidden">
        <MobileTopBar title="Studio" />
        <div className="mx-auto max-w-[1240px] px-5 py-8 sm:px-8 sm:py-10">
          {abandoned && abandoned.plan && abandoned.plan.length > 0 && (
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-brand/40 bg-brand/[0.06] px-6 py-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-md bg-brand text-white">
                  <RotateCcw className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-serif-display text-[17px] leading-tight text-brand-ivory">
                    Resume your checkout
                  </div>
                  <div className="mt-1 font-mono text-[10.5px] uppercase tracking-[0.24em] text-brand-mute">
                    {abandoned.plan.length} item{abandoned.plan.length !== 1 ? "s" : ""} &middot; {formatINR(abandoned.total)} &middot; saved just now
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate("/checkout")}
                  className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2.5 font-mono text-[10.5px] uppercase tracking-[0.28em] text-white hover:bg-[#ff5c26]"
                >
                  Resume checkout <ArrowRight className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => { clearAbandonedCheckout(); setAbandoned(null); }}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-brand-hair text-brand-mute hover:border-brand hover:text-brand"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-6 sm:gap-8">
            <div>
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand animate-pulse" />
                Studio dashboard · Live
              </div>
              <h1 className="mt-4 font-serif-display text-[44px] font-medium leading-[0.95] tracking-tight sm:text-[56px] lg:text-[64px]">
                Welcome back<span className="text-brand">.</span>
              </h1>
              <div className="mt-3 text-[13px] text-brand-ivory/60 break-words">
                Signed in as <span className="text-brand-ivory">{email}</span>
              </div>
            </div>

            <div className="hidden w-full grid-cols-2 gap-4 sm:grid sm:w-auto sm:grid-cols-4 sm:gap-6">
              <MetricCell number={String(SERVICES.length).padStart(2, "0")} label="Services" />
              <MetricCell number={String(totalSubServices())} label="Sub-features" />
              <MetricCell number={String(plan.length)} label="In your plan" accent />
              <MetricCell number="24h" label="Response" />
            </div>
          </div>

          {/* Quick actions */}
          <div className="mt-12">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
              <ArrowRight className="h-3 w-3 text-brand" />
              Quick Actions
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {QUICK_ACTIONS.map((qa) => (
                <button
                  key={qa.id}
                  onClick={() => navigate(qa.to)}
                  className="group relative flex flex-col rounded-md border border-brand-hair bg-brand-panel p-5 text-left transition-colors hover:border-brand/60"
                >
                  <div className="mb-6 flex items-center justify-between">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                      <ArrowUpRight className="h-4 w-4" />
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute group-hover:text-brand">
                      Start
                    </span>
                  </div>
                  <div className="font-serif-display text-[19px] font-medium leading-tight tracking-tight">
                    {qa.title}
                  </div>
                  <div className="mt-1 text-[12px] leading-relaxed text-brand-ivory/60">
                    {qa.hint}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Pricing strip */}
          <div className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-md border border-brand-hair bg-brand-panel px-6 py-5">
            <div className="max-w-[720px]">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                Transparent Pricing
              </div>
              <p className="mt-2 text-[13px] leading-relaxed text-brand-ivory/70">
                Every sub-service has an upfront INR price. Add what you need to your plan, we’ll confirm scope in 24 hours. Range across the studio: ₹15,000 — ₹4,50,000.
              </p>
            </div>
            <button
              onClick={openDrawer}
              className="inline-flex items-center gap-3 rounded-md border border-brand-hair px-5 py-3 font-mono text-[11px] uppercase tracking-[0.28em] text-brand-ivory transition-colors hover:border-brand hover:text-brand"
            >
              <span className="h-2 w-2 rounded-full bg-brand" />
              View Plan ({plan.length})
            </button>
          </div>

          {/* All services */}
          <div className="mt-14">
            <div className="mb-4 flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                All Services
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                Click to expand
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {SERVICES.map((svc) => (
                <ServiceSummaryCard key={svc.slug} svc={svc} />
              ))}
            </div>
          </div>

          <StudioFooter />
        </div>
      </main>

      <ConsultModal open={showConsult} onOpenChange={setShowConsult} />
    </div>
  );
}

function MetricCell({ number, label, accent = false }) {
  return (
    <div className="border-l border-brand-hair pl-4">
      <div className={`font-serif-display text-[38px] leading-none ${accent ? "text-brand" : "text-brand-ivory"}`}>
        {number}
      </div>
      <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.24em] text-brand-mute">
        {label}
      </div>
    </div>
  );
}

function ServiceSummaryCard({ svc }) {
  const Icon = ICONS[svc.icon] || BookOpen;
  const navigate = useNavigate();
  const { overrides } = useOverrides();
  return (
    <button
      onClick={() => navigate(`/service/${svc.slug}`)}
      className="group relative flex flex-col rounded-md border border-brand-hair bg-brand-panel p-6 text-left transition-colors hover:border-brand/60"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-md bg-brand/10 text-brand">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
            <span>{svc.id}</span>
            <span className="text-brand-mute/40">—</span>
            <span>{svc.subs.length} options</span>
          </div>
          <div className="mt-2 font-serif-display text-[22px] leading-tight tracking-tight text-brand-ivory">
            {svc.label}
          </div>
          <div className="mt-1 text-[12.5px] text-brand-ivory/60">{svc.tagline}</div>
          <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
            From <span className="text-brand-ivory">{svc.priceFrom}</span>
          </div>
        </div>
      </div>

      <div className="mt-5 space-y-2">
        {svc.subs.slice(0, 4).map((rawSub) => {
          const sub = applySubOverride(rawSub, overrides);
          return (
            <div key={sub.code} className="flex items-center justify-between border-t border-brand-hair/60 pt-2 text-[12.5px]">
              <div className="flex min-w-0 items-center gap-3">
                <span className="font-mono text-[10px] tracking-[0.22em] text-brand-mute">{sub.code}</span>
                <span className="truncate text-brand-ivory/85">{sub.title}</span>
              </div>
              <span className="font-mono text-[11px] text-brand-ivory/70">{sub.price}</span>
            </div>
          );
        })}
        {svc.subs.length > 4 && (
          <div className="pt-3 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
            + {svc.subs.length - 4} more →
          </div>
        )}
      </div>
    </button>
  );
}
